import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import LoadingScreen from "@/components/LoadingScreen";
import { formatDateTime, formatRole } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { ADMIN } from "@/constants/testIds";

const ROLES = ["user", "analyst_admin", "business_admin", "super_admin"];

export default function AdminUsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [creditsAdjust, setCreditsAdjust] = useState("");
  const [reason, setReason] = useState("");

  const load = () => api.get(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`).then(({ data }) => setUsers(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const saveRole = async (role) => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.patch(`/admin/users/${selected.id}`, { role });
      toast.success("Role updated.");
      await load();
      setSelected({ ...selected, role });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const saveStatus = async (statusV) => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.patch(`/admin/users/${selected.id}`, { status: statusV });
      toast.success("Status updated.");
      await load();
      setSelected({ ...selected, status: statusV });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const grantCredits = async () => {
    if (!selected || !creditsAdjust) return;
    setSaving(true);
    try {
      await api.patch(`/admin/users/${selected.id}`, {
        credits_adjust: Number(creditsAdjust), adjust_reason: reason,
      });
      toast.success("Credits granted.");
      setCreditsAdjust(""); setReason("");
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  if (!users) return <LoadingScreen />;

  const isSuper = me?.role === "super_admin";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 sqit-card overflow-hidden">
        <div className="p-4 border-b border-[#E5E7EB]">
          <input
            data-testid="admin-users-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search users…"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135]"
          />
        </div>
        <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-220px)]">
          <table className="min-w-[900px] w-full text-sm" data-testid={ADMIN.usersTable}>
            <thead className="sticky top-0 bg-gray-50 border-b border-[#E5E7EB] z-10">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Credits</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Joined</th>
              </tr>
            </thead>

            <tbody>
              {users.map((u) => (
                <tr
                  key={u.id}
                  data-testid={`admin-user-row-${u.id}`}
                  onClick={() => setSelected(u)}
                  className={`border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${selected?.id === u.id ? "bg-[#EAF4E3]/40" : ""
                    }`}
                >
                  <td className="px-6 py-3">
                    <div className="font-medium text-gray-900">{u.full_name}</div>
                    <div className="text-xs text-gray-500">{u.email}</div>
                  </td>
                  <td className="px-6 py-3 text-xs uppercase tracking-wide">{formatRole(u.role)}</td>
                  <td className="px-6 py-3">{u.available_credits} / {u.frozen_credits}f</td>
                  <td className="px-6 py-3 capitalize">{u.status}</td>
                  <td className="px-6 py-3 text-xs text-gray-500">{formatDateTime(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="sqit-card p-6" data-testid="admin-users-detail">
        {selected ? (
          <>
            <h3 className="font-display text-lg font-semibold text-gray-900">{selected.full_name}</h3>
            <p className="text-sm text-gray-600">{selected.email}</p>
            <p className="text-xs text-gray-500 mt-1">{selected.organization}</p>

            <div className="mt-5">
              <label className="text-xs font-semibold text-gray-700">Role</label>
              <select
                data-testid="admin-user-role-select"
                value={selected.role}
                disabled={!isSuper || saving}
                onChange={(e) => saveRole(e.target.value)}
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white disabled:bg-gray-100"
              >
                {ROLES.map((r) => <option key={r} value={r}>{formatRole(r)}</option>)}
              </select>
            </div>
            <div className="mt-4">
              <label className="text-xs font-semibold text-gray-700">Status</label>
              <select
                data-testid="admin-user-status-select"
                value={selected.status}
                disabled={!isSuper || saving}
                onChange={(e) => saveStatus(e.target.value)}
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white disabled:bg-gray-100"
              >
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
            <div className="mt-4 pt-4 border-t border-[#E5E7EB]">
              <label className="text-xs font-semibold text-gray-700">Grant credits</label>
              <div className="flex gap-2 mt-1">
                <input
                  data-testid="admin-user-credits-input"
                  type="number" min={1}
                  value={creditsAdjust}
                  onChange={(e) => setCreditsAdjust(e.target.value)}
                  className="w-24 border border-gray-300 rounded-md px-2 py-2 text-sm"
                />
                <input
                  data-testid="admin-user-credits-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason"
                  className="flex-1 border border-gray-300 rounded-md px-2 py-2 text-sm"
                />
              </div>
              <button
                onClick={grantCredits}
                data-testid="admin-user-credits-grant"
                disabled={saving || !isSuper || !creditsAdjust}
                className="mt-2 w-full bg-[#0B6936] text-white hover:bg-[#09582D] rounded-md px-4 py-2 text-sm font-semibold disabled:opacity-60"
              >
                Grant
              </button>
              {!isSuper && (
                <p className="mt-2 text-xs text-gray-500">Only super admin can modify user roles, status, or credits.</p>
              )}
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-500">Select a user to see details.</p>
        )}
      </div>
    </div>
  );
}
